// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * How wide a chip has to be for its label to fit.
 *
 * `powerFlowLayout` is a pure module and stays that way, so measuring text —
 * which needs a DOM — happens here and is handed in as the layout's `widthOf`.
 *
 * A fixed-width chip was the first increment's biggest legibility cost: on a
 * real site almost every name elided (`RPP-PDU-…`, `Power Distributi…`), and a
 * chip whose label is cut off conveys no more than an empty box does. Measuring
 * costs one canvas call per distinct label, cached, and the layout clamps the
 * result — so a pathological name still cannot stretch the drawing.
 */

/** Chip label: 12px semibold, matching `.sld-node__label`. */
import { SYMBOL_NODE_SIZE, isSymbolNode, type PowerSymbol } from '../../../config/powerFlowSymbols';

const CHIP_FONT = '600 12px "IBM Plex Sans", system-ui, -apple-system, sans-serif';

/** Group badge: same size, and the `× N` counter rides in the same line. */
const GROUP_FONT = '600 12px "IBM Plex Sans", system-ui, -apple-system, sans-serif';

/**
 * Horizontal chrome around a chip's label: 9px padding either side, the 14px
 * the status marker reserves at the top right, and the 18px one-line glyph
 * plus its 6px gap.
 *
 * The glyph has to be counted here or a chip sized before the symbol layer
 * existed would elide 24px earlier than it used to — the symbol would push the
 * label out rather than sit beside it.
 */
const CHIP_CHROME = 9 * 2 + 14 + 18 + 6;

/** A group badge carries a status marker and a chevron. */
const GROUP_CHROME = 9 * 2 + 14 + 14;

/**
 * Average character width, used when nothing on the page can be laid out.
 *
 * jsdom lays nothing out, so every test would otherwise measure zero. The
 * estimate only has to be monotonic in label length to be useful.
 */
const FALLBACK_CHAR_WIDTH = 7;

let ruler: HTMLSpanElement | null | undefined;

/**
 * A single offscreen span, reused for every measurement.
 *
 * A span rather than a canvas 2d context on purpose: jsdom implements neither,
 * but `getContext` reports that by writing a "not implemented" stack to the
 * virtual console on every call, whereas an unlaid-out element simply measures
 * zero — so the fallback below is reached quietly instead of burying real test
 * output. It also measures the font the page actually loaded rather than a font
 * string that has to be kept in sync by hand.
 *
 * `undefined` means "not built yet", `null` means "no document to build it in".
 */
const measuringRuler = (): HTMLSpanElement | null => {
  if (ruler !== undefined) return ruler;
  if (typeof document === 'undefined' || !document.body) {
    ruler = null;
    return ruler;
  }
  const span = document.createElement('span');
  span.setAttribute('aria-hidden', 'true');
  span.style.position = 'absolute';
  span.style.visibility = 'hidden';
  span.style.whiteSpace = 'pre';
  span.style.left = '-9999px';
  span.style.top = '0';
  document.body.appendChild(span);
  ruler = span;
  return ruler;
};

const cache = new Map<string, number>();

const measureText = (text: string, font: string): number => {
  const key = `${font}\u001f${text}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const span = measuringRuler();
  let width = 0;
  if (span) {
    span.style.font = font;
    span.textContent = text;
    width = span.getBoundingClientRect().width;
  }
  // Zero means nothing laid this out — jsdom, or a detached document. The
  // estimate only has to be monotonic in label length to keep the layout
  // sensible.
  if (width === 0 && text.length > 0) width = text.length * FALLBACK_CHAR_WIDTH;

  cache.set(key, width);
  return width;
};

/** Width a chip needs for `label`. */
export const measureChipWidth = (label: string): number =>
  Math.ceil(measureText(label, CHIP_FONT)) + CHIP_CHROME;

/**
 * Chrome around the text of a device drawn as its symbol: the symbol, the gap
 * to the text, the status mark's clearance and the trailing padding.
 */
const SYMBOL_NODE_CHROME = SYMBOL_NODE_SIZE + 6 + 14 + 8;

/** Width a device drawn as its symbol needs for `label` beside the symbol. */
export const measureSymbolNodeWidth = (label: string): number =>
  Math.ceil(measureText(label, CHIP_FONT)) + SYMBOL_NODE_CHROME;

/** Figure line under a label: 10px regular, matching `.sld-node__value`. */
const VALUE_FONT = '400 10px "IBM Plex Sans", system-ui, -apple-system, sans-serif';

/**
 * The widest figure lines a utility feed can carry (AB#34848): `V · A` and
 * `kW · PF`, sized for the MV feeds in the data inventory (13.8 kV, 1,200 A).
 * The feed's width is structural and cannot see live values, so it
 * reserves room for these two; a short name such as `U1` would otherwise size
 * the node to the name and elide the readout it was given.
 */
const UTILITY_READOUT_LINES = ['12.47 kV · 1,200 A', '10.48 MW · PF 0.95'] as const;

/** Width a utility feed needs: its label, or its readout, whichever is wider. */
export const measureUtilityNodeWidth = (label: string): number =>
  Math.max(
    measureSymbolNodeWidth(label),
    ...UTILITY_READOUT_LINES.map(
      line => Math.ceil(measureText(line, VALUE_FONT)) + SYMBOL_NODE_CHROME
    )
  );

/** Width for a device node: symbol-and-text when it has a symbol, a chip otherwise. */
export const measureDeviceWidth = (label: string, symbol: PowerSymbol): number =>
  symbol === 'utility'
    ? measureUtilityNodeWidth(label)
    : isSymbolNode(symbol)
      ? measureSymbolNodeWidth(label)
      : measureChipWidth(label);

/** Width a load-group badge needs for `<type> × <count>`. */
export const measureGroupWidth = (typeLabel: string, count: number): number =>
  Math.ceil(measureText(`${typeLabel} × ${count}`, GROUP_FONT)) + GROUP_CHROME;
